/**
 * Step and substep finding utilities
 */
import { getStepId, getSubstepId } from './getters';
import { isSpecialStep } from './stepValidation';

const COMPLETED_STATUSES = ['Completed', 'Confirmed'];

/**
 * Finds the parent step for a given substep
 * @param {Array} stepsState - Array of steps
 * @param {string} substepCode - Substep code to find
 * @returns {Object|undefined} Parent step or undefined
 */
export const findStepForSubstep = (stepsState, substepCode) => {
  return stepsState.find((s) => s.substeps?.some((sub) => sub.code === substepCode));
};

/**
 * Finds the first non-special substep (skips intro and review steps)
 * @param {Array} flattenedSubsteps - Flattened array of substeps
 * @param {Array} stepsState - Array of steps
 * @returns {Object|undefined} First real substep or undefined
 */
export const findFirstRealSubstep = (flattenedSubsteps, stepsState) => {
  return flattenedSubsteps.find((sub) => {
    const stepForSub = findStepForSubstep(stepsState, sub.code);
    const stepId = getStepId(stepForSub);
    return !isSpecialStep(stepId);
  });
};

/**
 * Checks whether any non-special step has been completed
 * @param {Array} stepsState - Array of steps
 * @returns {boolean} True if any real step is completed
 */
export const hasAnyCompletedStep = (stepsState) => {
  return stepsState.some((step) => {
    const stepId = getStepId(step);
    return !isSpecialStep(stepId) && COMPLETED_STATUSES.includes(step.status);
  });
};

/**
 * Finds the first non-special substep whose parent step is not completed.
 * Used to resume the wizard at the first incomplete step when returning.
 * @param {Array} flattenedSubsteps - Flattened array of substeps
 * @param {Array} stepsState - Array of steps
 * @returns {Object|undefined} First incomplete substep or undefined
 */
export const findFirstIncompleteSubstep = (flattenedSubsteps, stepsState) => {
  return flattenedSubsteps.find((sub) => {
    const stepForSub = findStepForSubstep(stepsState, sub.code);
    if (!stepForSub) return false;
    const stepId = getStepId(stepForSub);
    return !isSpecialStep(stepId) && !COMPLETED_STATUSES.includes(stepForSub.status);
  });
};

/**
 * Finds a step by its ID
 * @param {Array} stepsState - Array of steps
 * @param {string|number} stepId - Step ID to find
 * @returns {Object|undefined} Step or undefined
 */
export const findStepById = (stepsState, stepId) => {
  return stepsState.find((s) => String(getStepId(s)) === String(stepId));
};

/**
 * Finds a substep by its ID within a step
 * @param {Object} step - Step object
 * @param {string|number} substepId - Substep ID to find
 * @returns {Object|undefined} Substep or undefined
 */
export const findSubstepById = (step, substepId) => {
  return step?.substeps?.find((sub) => String(getSubstepId(sub)) === String(substepId));
};
